import React, { useEffect, useState } from 'react';
import i18n from '../i18n';
import { api } from '../lib/api';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from './ui/dropdown-menu';
import { ChevronDown, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import StageTransitionModal, { STAGES_WITH_MODAL } from './StageTransitionModal';
import { logger } from '../lib/logger';

/**
 * PipelineStageControl — shows the current pipeline v2 stage as a colored
 * chip and lets the user move the lead to ANY other stage (open the
 * StageTransitionModal when metadata is required).
 */
export default function PipelineStageControl({ lead, onUpdated }) {
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState({ open: false, toStage: null, toStageLabel: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/pipeline/config');
        if (!cancelled) setStages(r.data?.stages || []);
      } catch (err) {
        logger.error('Failed to load pipeline config:', err);
      } finally {
        if (!cancelled) setLoadingStages(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const current = stages.find((s) => s.key === lead.stage);
  const currentLabel = current?.label || lead.stage || 'Sconosciuto';
  const currentColor = current?.color || '#94a3b8';

  const directTransition = async (toStage, metadata = {}) => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/transition`, { to_stage: toStage, metadata });
      toast.success(`Stage → ${stages.find((s) => s.key === toStage)?.label || toStage}`);
      onUpdated?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || i18n.t('pipeline.stageUpdateError', 'Errore aggiornamento stage');
      toast.error(typeof msg === 'string' ? msg : i18n.t('pipeline.stageUpdateError', 'Errore aggiornamento stage'));
    } finally {
      setBusy(false);
    }
  };

  const handleStageClick = (stage) => {
    if (stage.key === lead.stage) return; // No-op on same stage
    if (STAGES_WITH_MODAL.has(stage.key)) {
      setModal({ open: true, toStage: stage.key, toStageLabel: stage.label });
    } else {
      directTransition(stage.key);
    }
  };

  const handleModalConfirm = async ({ to_stage, metadata }) => {
    await directTransition(to_stage, metadata);
  };

  return (
    <div className="space-y-2" data-testid="pipeline-stage-control">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Stage pipeline</div>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          className="text-xs font-medium px-2.5 py-1"
          style={{ backgroundColor: currentColor, color: '#fff' }}
          data-testid="current-stage-badge"
        >
          {currentLabel}
        </Badge>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={loadingStages || busy}
              data-testid="change-stage-trigger"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              Cambia stage
              <ChevronDown className="w-3.5 h-3.5 ml-1" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60">
            <DropdownMenuLabel className="text-xs">Sposta a:</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {stages.map((s) => {
              const isCurrent = s.key === lead.stage;
              return (
                <DropdownMenuItem
                  key={s.key}
                  onClick={() => handleStageClick(s)}
                  disabled={isCurrent}
                  data-testid={`stage-option-${s.key}`}
                  className="flex items-center justify-between gap-2 cursor-pointer"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: s.color }}
                    />
                    <span className="text-xs truncate">{s.label}</span>
                  </div>
                  {isCurrent && <span className="text-[9px] text-muted-foreground">attuale</span>}
                  {STAGES_WITH_MODAL.has(s.key) && !isCurrent && (
                    <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {current?.description && (
        <p className="text-[10px] text-muted-foreground italic">{current.description}</p>
      )}

      <StageTransitionModal
        open={modal.open}
        onOpenChange={(open) => setModal((m) => ({ ...m, open }))}
        lead={lead}
        toStage={modal.toStage}
        toStageLabel={modal.toStageLabel}
        onConfirm={handleModalConfirm}
      />
    </div>
  );
}
